import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { 
  Package, 
  User as UserIcon, 
  Truck, 
  CreditCard, 
  Info, 
  MapPin, 
  Phone, 
  Clock,
  ArrowLeft,
  Copy,
  Check,
  AlertCircle,
  CheckCircle,
  X
} from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import DeliveryPointDisplay from '../components/store/CheckoutModal/components/DeliveryPointDisplay';
import TBankPayment from '../components/payments/TBankPayment';
import { PublicCheckout } from '../api/publicEntities';

const PublicOrderPage = () => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showTBank, setShowTBank] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [tbankSuccessAlert, setTbankSuccessAlert] = useState(false);
  const [tbankFailAlert, setTbankFailAlert] = useState(false);

  // Проверка, требует ли метод оплаты онлайн-инициирования
  const requiresOnlinePayment = useCallback(() => {
    if (!order || !order.payment_method) return true;
    const paymentMethod = order.payment_method.toLowerCase();
    const offlineMethods = ['cash', 'наличные', 'наличные при получении'];
    return !offlineMethods.some(method => paymentMethod.includes(method.toLowerCase()));
  }, [order]);

  const isOrderUnpaid = useCallback(() => {
    return order && order.status === 'ordered';
  }, [order]);

  const handlePayClick = useCallback(() => {
    // Проверяем, требует ли метод оплаты онлайн-инициирования
    if (!requiresOnlinePayment()) {
      setPaymentError('Этот метод оплаты не требует онлайн-оплаты. Оплата будет произведена при получении заказа.');
      return;
    }
    setShowTBank(true);
    setPaymentError(null);
    // Scroll to payment section
    setTimeout(() => {
      const paymentCard = document.getElementById('payment-card');
      if (paymentCard) {
        paymentCard.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  }, [requiresOnlinePayment]);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const orderData = await PublicCheckout.getOrderById(id);
        setOrder(orderData);
      } catch (err) {
        setError(err.message || 'Failed to load order');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchOrder();
    }
  }, [id]);

  // Handle T-Bank payment URL parameters
  useEffect(() => {
    const tbankPayment = searchParams.get('tbank-payment');
    
    if (tbankPayment === 'success') {
      setTbankSuccessAlert(true);
      // Clear URL parameter to prevent showing alert on page refresh
      setSearchParams({}, { replace: true });
    } else if (tbankPayment === 'fail') {
      setTbankFailAlert(true);
      // Clear URL parameter to prevent showing alert on page refresh
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Handle go-to-payment flag
  useEffect(() => {
    const goToPayment = searchParams.get('go-to-payment');
    
    if (goToPayment === 'true' && order && isOrderUnpaid() && requiresOnlinePayment()) {
      // Remove parameter from URL to prevent repeated triggers
      setSearchParams({}, { replace: true });
      // Call payment function
      handlePayClick();
    }
  }, [searchParams, order, setSearchParams, handlePayClick, isOrderUnpaid, requiresOnlinePayment]);


  const getStatusBadge = (status) => {
    const statusConfig = {
      ordered: { variant: "secondary", className: "bg-blue-100 text-blue-800" },
      paid: { variant: "secondary", className: "bg-green-100 text-green-800" },
      signed: { variant: "secondary", className: "bg-purple-100 text-purple-800" },
      packed: { variant: "secondary", className: "bg-orange-100 text-orange-800" },
      shipped: { variant: "secondary", className: "bg-yellow-100 text-yellow-800" },
      delivered: { variant: "secondary", className: "bg-green-100 text-green-800" }
    };
    
    const config = statusConfig[status] || { variant: "secondary", className: "bg-gray-100 text-gray-800" };
    
    return (
      <Badge variant={config.variant} className={config.className}>
        {t(`public_order.status.${status}`, status?.charAt(0).toUpperCase() + status?.slice(1) || 'Unknown')}
      </Badge>
    );
  };

  const getDeliveryMethodInfo = () => {
    if (!order?.shipment_details) return null;
    
    const { type, data } = order.shipment_details;
    
    switch (type) {
      case 'self_pickup':
        return {
          label: t('public_order.delivery_methods.self_pickup'),
          details: data.address,
          cost: data.price,
        };
      case 'cdek_delivery_point':
        return {
          label: t('public_order.delivery_methods.cdek_delivery_point'),
          details: data.deliveryPoint,
          cost: data.price,
        };
      case 'cdek_courier':
        return {
          label: t('public_order.delivery_methods.cdek_courier'),
          details: data.address,
          cost: data.price,
        };
      default:
        return null;
    }
  };

  const copyOrderNumber = async () => {
    try {
      await navigator.clipboard.writeText(order.order_number);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy order number:', err);
    }
  };

  const handlePaymentSuccess = (paymentData) => {
    console.log('Payment successful:', paymentData);
    // Refresh order data to get updated status
    window.location.reload();
  };

  const handlePaymentError = (error) => {
    console.error('Payment error:', error);
    setPaymentError(error);
  };

  const closeTbankSuccessAlert = () => {
    setTbankSuccessAlert(false);
  };

  const closeTbankFailAlert = () => {
    setTbankFailAlert(false);
  };

  const formatCurrency = (amount) => {
    return `₽${Number(amount || 0).toLocaleString()}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMM d, yyyy');
    } catch {
      return 'N/A';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('public_order.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            <p className="font-bold">{t('public_order.error.title')}</p>
            <p>{error}</p>
          </div>
          <Button onClick={() => window.location.href = '/'}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('public_order.back_to_store')}
          </Button>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4">
            <p className="font-bold">{t('public_order.error.no_data')}</p>
            <p>{t('public_order.error.no_data_description')}</p>
          </div>
          <Button onClick={() => window.location.href = '/'}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('public_order.back_to_store')}
          </Button>
        </div>
      </div>
    );
  }

  const deliveryInfo = getDeliveryMethodInfo();

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{t('public_order.title')}</h1>
              <p className="text-gray-600 mt-1">{t('public_order.order_id')}: {id}</p>
            </div>
            <a 
              href="/" 
              className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('public_order.back_to_store')}
            </a>
          </div>

          {/* T-Bank Success Alert */}
          {tbankSuccessAlert && (
            <Alert className="border-green-200 bg-green-50">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-green-800">{t('public_order.payment.tbank_alerts.success_title')}</p>
                  <p className="text-sm text-green-700">{t('public_order.payment.tbank_alerts.success_message')}</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={closeTbankSuccessAlert}
                  className="text-green-600 hover:text-green-700 hover:bg-green-100"
                >
                  <X className="w-4 h-4" />
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {/* T-Bank Fail Alert */}
          {tbankFailAlert && (
            <Alert variant="destructive" className="border-red-200 bg-red-50">
              <AlertCircle className="h-4 w-4 text-red-600" />
              <AlertDescription className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-red-800">{t('public_order.payment.tbank_alerts.fail_title')}</p>
                  <p className="text-sm text-red-700">{t('public_order.payment.tbank_alerts.fail_message')}</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={closeTbankFailAlert}
                  className="text-red-600 hover:text-red-700 hover:bg-red-100"
                >
                  <X className="w-4 h-4" />
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {/* Unpaid Order Alert */}
          {isOrderUnpaid() && (
            <Alert className="border-amber-200 bg-amber-50">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-amber-800">
                    {requiresOnlinePayment() 
                      ? t('public_order.payment.unpaid_alert')
                      : 'Заказ создан. Оплата будет произведена при получении.'}
                  </p>
                  <p className="text-sm text-amber-700">
                    {requiresOnlinePayment() 
                      ? t('public_order.payment.unpaid_description')
                      : 'Вы выбрали оплату наличными. Оплатите заказ при получении.'}
                  </p>
                </div>
                {requiresOnlinePayment() && (
                  <Button
                    onClick={handlePayClick}
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                  >
                    {t('public_order.payment.pay_button')}
                  </Button>
                )}
              </AlertDescription>
            </Alert>
          )}

          {/* Order Header Card */}
          <Card className="border-slate-200">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Package className="w-5 h-5 text-slate-600" />
                  {t('public_order.header.title')}
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={copyOrderNumber}
                    className="flex items-center gap-2"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copied ? t('public_order.copied') : t('public_order.copy_order_number')}
                  </Button>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm font-medium text-slate-500">{t('public_order.header.order_number')}</p>
                <p className="font-semibold text-slate-900 text-lg">
                  {order.order_number || 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500">{t('public_order.header.status')}</p>
                <div className="mt-1">
                  {getStatusBadge(order.status)}
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500">{t('public_order.header.order_date')}</p>
                <p className="font-semibold text-slate-900">
                  {formatDate(order.order_date)}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500">{t('public_order.header.total_amount')}</p>
                <p className="font-semibold text-slate-900 text-lg text-amber-600">
                  {formatCurrency(order.total_amount)}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Customer Information Card */}
          <Card className="border-slate-200">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <UserIcon className="w-5 h-5 text-slate-600" />
                {t('public_order.customer.title')}
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="text-sm font-medium text-slate-500">{t('public_order.customer.name')}</p>
                <p className="font-semibold text-slate-900">{order.customer_name}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500">{t('public_order.customer.email')}</p>
                <p className="font-semibold text-slate-900">{order.customer_email}</p>
              </div>
            </CardContent>
          </Card>

          {/* Order Items Card */}
          <Card className="border-slate-200">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <Package className="w-5 h-5 text-slate-600" />
                {t('public_order.items.title')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {order.items?.map((item, index) => (
                  <div key={index} className="flex justify-between items-center py-2">
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">{item.product_name}</p>
                      <p className="text-sm text-slate-500">{t('public_order.items.type')}: {item.type || 'N/A'}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-slate-600">
                        {item.quantity} × {formatCurrency(item.unit_price)}
                      </p>
                      <p className="font-semibold text-slate-900">
                        {formatCurrency(item.total_price)}
                      </p>
                    </div>
                  </div>
                ))}
                
                <Separator className="my-4" />
                
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>{t('public_order.items.subtotal')}</span>
                    <span>{formatCurrency(order.total_amount - (order.shipping_cost || 0))}</span>
                  </div>
                  {order.shipping_cost > 0 && (
                    <div className="flex justify-between text-sm">
                      <span>{t('public_order.items.shipping')}</span>
                      <span>{formatCurrency(order.shipping_cost)}</span>
                    </div>
                  )}
                  <Separator />
                  <div className="flex justify-between font-bold text-lg">
                    <span>{t('public_order.items.total')}</span>
                    <span className="text-amber-600">{formatCurrency(order.total_amount)}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Shipping Information Card */}
          {deliveryInfo && (
            <Card className="border-slate-200">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Truck className="w-5 h-5 text-slate-600" />
                  {t('public_order.shipping.title')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-500">{t('public_order.shipping.delivery_method')}</p>
                    <p className="font-semibold text-slate-900">{deliveryInfo.label}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-slate-500">{t('public_order.shipping.cost')}</p>
                    <p className="font-semibold text-slate-900">
                      {deliveryInfo.cost > 0 ? formatCurrency(deliveryInfo.cost) : t('public_order.shipping.free')}
                    </p>
                  </div>
                </div>

                {order.shipment_details?.type === 'cdek_delivery_point' && deliveryInfo.details && (
                  <DeliveryPointDisplay
                    point={deliveryInfo.details}
                    showChangeButton={false}
                  />
                )}

                {order.shipment_details?.type === 'cdek_courier' && deliveryInfo.details && (
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-slate-700">
                      <div className="font-medium">{t('public_order.shipping.delivery_address')}</div>
                      <div>{deliveryInfo.details}</div>
                    </div>
                  </div>
                )}

                {order.shipment_details?.type === 'self_pickup' && deliveryInfo.details && (
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-slate-700">
                      <div className="font-medium">{t('public_order.shipping.pickup_location')}</div>
                      <div>{deliveryInfo.details}</div>
                    </div>
                  </div>
                )}

                {order.tracking_number && (
                  <div className="flex items-center gap-2">
                    <Package className="w-4 h-4 text-slate-500" />
                    <div className="text-sm text-slate-700">
                      <span className="font-medium">{t('public_order.shipping.tracking_number')}: </span>
                      <span className="font-mono">{order.tracking_number}</span>
                    </div>
                  </div>
                )}

                {(order.expected_delivery_date || order.actual_delivery_date) && (
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-slate-500" />
                    <div className="text-sm text-slate-700">
                      {order.actual_delivery_date ? (
                        <div>
                          <span className="font-medium">{t('public_order.shipping.delivered')}: </span>
                          <span>{formatDate(order.actual_delivery_date)}</span>
                        </div>
                      ) : (
                        <div>
                          <span className="font-medium">{t('public_order.shipping.expected_delivery')}: </span>
                          <span>{formatDate(order.expected_delivery_date)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Payment Information Card */}
          <Card id="payment-card" className="border-slate-200">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-slate-600" />
                {t('public_order.payment.title')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm font-medium text-slate-500">{t('public_order.payment.payment_method')}</p>
                  <p className="font-semibold text-slate-900">
                    {order.payment_method || 'N/A'}
                  </p>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-500">{t('public_order.payment.payment_status')}</p>
                    <p className="font-semibold text-slate-900">
                      {order.status !== 'ordered' ? t('public_order.payment.paid') : t('public_order.payment.pending')}
                    </p>
                  </div>
                  {isOrderUnpaid() && requiresOnlinePayment() && (
                    <Button
                      onClick={handlePayClick}
                      className="bg-amber-600 hover:bg-amber-700 text-white"
                    >
                      {t('public_order.payment.pay_button')}
                    </Button>
                  )}
                </div>
              </div>

              {/* T-Bank Payment Component */}
              {showTBank && isOrderUnpaid() && requiresOnlinePayment() && (
                <div className="mt-4">
                  <TBankPayment
                    orderId={order.id}
                    onPaymentSuccess={handlePaymentSuccess}
                    onPaymentError={handlePaymentError}
                    isVisible={showTBank}
                  />
                </div>
              )}

              {/* Payment Error Display */}
              {paymentError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{paymentError}</AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* Additional Details Card */}
          <Card className="border-slate-200">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <Info className="w-5 h-5 text-slate-600" />
                {t('public_order.details.title')}
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="text-sm font-medium text-slate-500">{t('public_order.details.order_created')}</p>
                <p className="font-semibold text-slate-900">
                  {formatDate(order.created_date)}
                </p>
              </div>
              {order.notes && (
                <div className="md:col-span-2">
                  <p className="text-sm font-medium text-slate-500">{t('public_order.details.notes')}</p>
                  <p className="font-semibold text-slate-900">{order.notes}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PublicOrderPage;
